import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Menu, Select, Avatar } from 'antd';
import type { MenuProps } from 'antd';
import styled from 'styled-components';
import { UserOutlined, SearchOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { saveSidebarCollapsed } from '../../utils/storageUtils';
import {
  DashboardIcon,
  FinancialIcon,
  HierarchyIcon,
  RiskIcon,
  ControlIcon,
  GapIcon,
  CertificationIcon,
  COSOIcon,
  AuditIcon,
  SystemSettingsIcon,
  LogoutIcon,
} from './MenuIcons';
import ConfirmModal from '../common/ConfirmModal';
import { AppDispatch, RootState, resetReduxState } from '../../store';
import { setSelectedReportByYear } from '../../store/slices/annualReportSlice';
import { clearChangeLogCache } from '../../store/slices/changeLogSlice';
import { RESPONSIVE_BREAKPOINT } from '../../utils/constants';
import warningIcon from '../../assets/icons/ic-warning.svg';
import { signOut } from '../../services/authService';
import { broadcastLogout } from '../../utils/authEventBroadcast';

const SidebarContainer = styled.div<{ $collapsed?: boolean }>`
  width: ${props => props.$collapsed ? '0px' : '280px'};
  min-width: ${props => props.$collapsed ? '0px' : '280px'};
  height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  border-right: ${props => props.$collapsed ? 'none' : '1px solid #F0F0F0'};
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1000;
  overflow: hidden;
  transition: width 0.25s ease, min-width 0.25s ease;

  @media (max-width: ${RESPONSIVE_BREAKPOINT}) {
    display: none;
  }
`;

const CollapseToggle = styled.button<{ $collapsed?: boolean }>`
  position: fixed;
  top: 12px;
  left: ${props => props.$collapsed ? '8px' : '268px'};
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid #E5E7EB;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1001;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  transition: left 0.25s ease, background 0.2s;
  padding: 0;

  &:hover {
    background: #EFF6FF;
    border-color: #2563EB;
  }

  .anticon {
    font-size: 10px;
    color: #6B7280;
  }

  &:hover .anticon {
    color: #2563EB;
  }

  @media (max-width: ${RESPONSIVE_BREAKPOINT}) {
    display: none;
  }
`;

const LogoSection = styled.div`
  padding: 12px 20px 0px 20px;
  border-bottom: 1px solid #F0F0F0;
`;

const Logo = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 18px;
  color: #2563EB;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const FiscalYearSection = styled.div`
  padding: 8px 20px;
`;

const CompanySection = styled.div`
  padding: 8px 20px 0px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  .company-info {
    flex: 1;
    justify-content: center;
    display: flex;

    .company-name {
      font-weight: 600;
      font-size: 16px;
      color: #1F2937;
      margin: 0;
    }
  }
`;

const CompanyAvatar = styled(Avatar)`
  background: #DBEAFE;
  font-weight: 600;
  flex-shrink: 0;
  border-radius: 8px;
  color: #2563EB;
`;

const CompanyLogo = styled.div`
  max-height: 80px;
  max-width: 160px;
  flex-shrink: 0;
  display: flex;
  align-items: center;

  img {
    height: 100%;
    max-width: 100%;
    object-fit: contain;
    border-radius: 8px;
  }
`;

const NavigationSection = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0px;
  border-top: 1px solid #F0F0F0;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #D1D5DB;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9CA3AF;
  }

  .ant-menu {
    border: none !important;
    border-inline-end: none !important;
    background: transparent;
  }

  .ant-menu-item {
    border-radius: 6px;
    height: 48px;
    line-height: 24px;
    font-size: 16px;
    color: #1F2937;

    &:hover {
      background: #F3F4F6;
    }

    &.ant-menu-item-selected {
      background: transparent;
      color: #2563EB;
      font-weight: 500;

      .ant-menu-item-icon {
        color: #2563EB;
        
        svg path {
          stroke: #2563EB;
        }
      }

      &::after {
        display: none;
      }
    }

    .ant-menu-title-content {
      margin-left: 12px !important;
    }
  }

  .ant-menu-item-icon {
    svg {
      width: 20px !important;
      height: 20px !important;
    }
  }

  .ant-menu-item-divider {
    margin: 8px 0;
    background: #E5E7EB;
  }
`;

const UserSection = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #F0F0F0;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .user-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;

    .user-details {
      flex: 1;

      .user-name {
        font-weight: 600;
        font-size: 14px;
        color: #1F2937;
        margin: 0;
      }

      .user-role {
        font-weight: 400;
        font-size: 12px;
        color: #6B7280;
        margin: 0;
      }
    }
  }

  .settings-icon {
    font-size: 18px;
    color: #6B7280;
    cursor: pointer;
    padding: 8px;
    border-radius: 6px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: #EFF6FF;
      color: #2563EB;
    }

    &:active {
      transform: scale(0.95);
    }
  }
`;

export interface SidebarProps {
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, onCollapsedChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { user, isAdmin } = useSelector((state: RootState) => state.auth);
  const { reports, selectedReport } = useSelector((state: RootState) => state.annualReport);

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleToggleCollapse = () => {
    const next = !collapsed;
    saveSidebarCollapsed(next);
    onCollapsedChange?.(next);
  };

  // Handle year change
  const handleYearChange = (year: number) => {
    dispatch(clearChangeLogCache()); // Clear change log cache when fiscal year changes
    dispatch(setSelectedReportByYear(year));
  };

  const menuItems: MenuProps['items'] = [
    {
      key: '/search',
      icon: <SearchOutlined style={{ fontSize: '20px' }} />,
      label: 'Search',
    },
    {
      type: 'divider' as const,
    },
    ...(isAdmin ? [{
      key: '/dashboard',
      icon: <DashboardIcon />,
      label: 'Dashboard Reporting',
    }] : []),
    ...(isAdmin ? [{
      key: '/financial-statements',
      icon: <FinancialIcon />,
      label: 'Financial Statements',
    }] : []),
    {
      key: '/process-hierarchy',
      icon: <HierarchyIcon />,
      label: 'Process Hierarchy',
    },
    {
      key: '/risk-assessment',
      icon: <RiskIcon />,
      label: 'Risk Assessment',
    },
    {
      key: '/control-activities',
      icon: <ControlIcon />,
      label: 'Control Activities',
    },
    {
      key: '/gaps',
      icon: <GapIcon />,
      label: 'Gaps',
    },
    {
      key: '/certification',
      icon: <CertificationIcon />,
      label: 'Certification',
    },
    ...(isAdmin ? [{
      key: '/coso-compliance',
      icon: <COSOIcon />,
      label: 'COSO Compliance',
    }] : []),
    // Audit Plan - Only visible to admins
    ...(isAdmin ? [{
      key: '/audit-plan',
      icon: <AuditIcon />,
      label: 'Audit Plan',
    }] : []),
    {
      type: 'divider' as const,
    },
    {
      key: '/settings',
      icon: <SystemSettingsIcon />,
      label: 'Settings',
    },
    {
      key: 'logout',
      icon: <LogoutIcon />,
      label: 'Log out',
      onClick: () => {
        setShowLogoutModal(true);
      },
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    // Don't navigate for logout as it has its own onClick handler
    if (key === 'logout') return;
    navigate(key);
  };

  const handleAccountSettings = () => {
    navigate('/settings');
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    try {
      // Call server-side sign out
      await signOut();
      // Broadcast logout to all tabs
      broadcastLogout('user_initiated');
      // Clear all Redux state with single action
      dispatch(resetReduxState());
      // Navigate to sign in
      navigate('/auth/signin');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear all Redux state even if server call fails
      dispatch(resetReduxState());
      navigate('/auth/signin');
    } finally {
      setIsLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  // Get active menu key based on current path
  const getActiveMenuKey = () => {
    const path = location.pathname;
    
    // Check for exact matches first
    const menuKeys = [
      '/dashboard',
      '/financial-statements',
      '/process-hierarchy',
      '/risk-assessment',
      '/control-activities',
      '/gaps',
      '/certification',
      '/coso-compliance',
      '/audit-plan',
      '/settings',
    ];
    
    // Check exact match
    if (menuKeys.includes(path)) {
      return path;
    }
    
    // Check if path starts with any menu key (for nested routes)
    for (const key of menuKeys) {
      if (path.startsWith(key)) {
        return key;
      }
    }
    
    return path;
  };

  const currentPath = getActiveMenuKey();

  return (
    <>
      <CollapseToggle $collapsed={collapsed} onClick={handleToggleCollapse}>
        {collapsed ? <RightOutlined /> : <LeftOutlined />}
      </CollapseToggle>
      <SidebarContainer $collapsed={collapsed} data-no-highlight>
        <LogoSection>
          <Logo>OSA Internal Controls</Logo>
        </LogoSection>

        <CompanySection>
          <div className="company-info">
            <p className="company-name">{user?.companyName?.trim() || 'Company'}</p>
          </div>
        </CompanySection>

        <CompanySection>
          {user?.companyImageUrl ? (
            <CompanyLogo>
              <img
                src={"data:image/png;base64," + user.companyImageUrl}
                alt={user?.companyName || 'Company'}
              />
            </CompanyLogo>
            ) : (
              <CompanyAvatar size={40}>
                {(user?.companyName?.trim()?.charAt(0) || 'C').toUpperCase()}
              </CompanyAvatar>
            )}
        </CompanySection>

        <FiscalYearSection>
          <Select
            value={selectedReport?.year}
            onChange={handleYearChange}
            style={{ width: '100%' }}
            options={reports.map(report => ({
              value: report.year,
              label: `Fiscal Year ${report.year}`,
            }))}
          />
        </FiscalYearSection>

          
        

        <NavigationSection>
          <Menu
            mode="inline"
            selectedKeys={[currentPath]}
            items={menuItems}
            onClick={handleMenuClick}
          />
        </NavigationSection>

        <UserSection>
          <div className="user-info">
            <Avatar 
              size={36} 
              icon={<UserOutlined />}
              style={{ borderRadius: '8px' }}
            />
            <div className="user-details">
              <p className="user-name">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="user-role">{user?.userType}</p>
            </div>
          </div>
          <div 
            className="settings-icon" 
            onClick={handleAccountSettings}
          >
            <SystemSettingsIcon />
          </div>
        </UserSection>
      </SidebarContainer>

      <ConfirmModal
        open={showLogoutModal}
        onClose={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
        icon={warningIcon}
        title="Sign out"
        description="Are you sure you would like to sign out of your OSA Internal Controls account?"
        confirmText="Sign out"
        cancelText="Cancel"
        loading={isLoggingOut}
      />
    </>
  );
};

export default Sidebar;

